import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GitBranch, ArrowRightLeft } from "lucide-react";
import { StructureCard } from "@/components/holding/StructureCard";
import { IcReconciliationCard } from "@/components/holding/IcReconciliationCard";
import { ConsolidationCard } from "@/components/holding/ConsolidationCard";
import { VatGroupCard } from "@/components/holding/VatGroupCard";

export default function Holding() {
  const companyId = localStorage.getItem("demoCompanyId");
  const [companies, setCompanies] = useState([]);
  const [structure, setStructure] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [showIc, setShowIc] = useState(false);
  const [icForm, setIcForm] = useState({ to_company_id: "", amount: "", description: "" });

  const otherCompanies = (companies || []).filter((c) => (c._id || c.id) !== companyId);

  const loadStructure = useCallback(async () => {
    const r = await apiClient.get(`/companies/${companyId}/holding/structure`);
    setStructure(r.data);
  }, [companyId]);

  useEffect(() => {
    loadStructure().catch(() => {});
    apiClient.get("/my/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, [loadStructure]);

  const createIc = async () => {
    try {
      await apiClient.post(`/companies/${companyId}/holding/intercompany-invoice`, {
        to_company_id: icForm.to_company_id,
        amount: parseFloat(icForm.amount),
        description: icForm.description,
      });
      toast.success("Intercompany-Rechnung in beiden Firmen gebucht ✓ (1180 / 2080)");
      setShowIc(false);
      setIcForm({ to_company_id: "", amount: "", description: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="holding-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitBranch className="h-6 w-6 text-violet-600" />
            Holding &amp; Konzern
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Konzernstruktur, Intercompany-Abgleich, konsolidierte Bilanz/ER mit Kapitalkonsolidierung und MWST-Gruppenbesteuerung.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Jahr</Label>
          <Input type="number" className="w-24" value={year} onChange={(e) => setYear(e.target.value)} data-testid="holding-year-input" />
          <Button onClick={() => setShowIc(true)} data-testid="holding-ic-invoice-btn">
            <ArrowRightLeft className="h-4 w-4 mr-1.5" /> Intercompany-Rechnung
          </Button>
        </div>
      </div>

      <Tabs defaultValue="structure" className="space-y-4">
        <TabsList data-testid="holding-tabs">
          <TabsTrigger value="structure" data-testid="holding-tab-structure">Struktur</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="holding-tab-reconciliation">IC-Abgleich</TabsTrigger>
          <TabsTrigger value="consolidation" data-testid="holding-tab-consolidation">Konsolidierung</TabsTrigger>
          <TabsTrigger value="vat" data-testid="holding-tab-vat">MWST-Gruppe</TabsTrigger>
        </TabsList>
        <TabsContent value="structure">
          <StructureCard companyId={companyId} structure={structure} companies={companies} onChanged={loadStructure} />
        </TabsContent>
        <TabsContent value="reconciliation">
          <IcReconciliationCard companyId={companyId} year={year} />
        </TabsContent>
        <TabsContent value="consolidation">
          <ConsolidationCard companyId={companyId} year={year} />
        </TabsContent>
        <TabsContent value="vat">
          <VatGroupCard companyId={companyId} year={year} />
        </TabsContent>
      </Tabs>

      <Dialog open={showIc} onOpenChange={setShowIc}>
        <DialogContent data-testid="holding-ic-dialog">
          <DialogHeader><DialogTitle>Intercompany-Rechnung</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Bucht in <b>beiden</b> Firmen gleichzeitig: hier <span className="font-mono">1180 an Ertrag</span>,
              beim Empfänger <span className="font-mono">Aufwand an 2080</span>.
            </p>
            <div>
              <Label className="text-sm">Empfänger-Firma</Label>
              <Select value={icForm.to_company_id} onValueChange={(v) => setIcForm({ ...icForm, to_company_id: v })}>
                <SelectTrigger data-testid="ic-company-select"><SelectValue placeholder="Firma wählen…" /></SelectTrigger>
                <SelectContent>
                  {otherCompanies.map((c) => (
                    <SelectItem key={c._id || c.id} value={c._id || c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Betrag CHF</Label>
              <Input type="number" step="0.05" value={icForm.amount} onChange={(e) => setIcForm({ ...icForm, amount: e.target.value })}
                placeholder="5000.00" data-testid="ic-amount-input" />
            </div>
            <div>
              <Label className="text-sm">Beschreibung</Label>
              <Input value={icForm.description} onChange={(e) => setIcForm({ ...icForm, description: e.target.value })}
                placeholder="z.B. Management Fee Q3 2026" data-testid="ic-description-input" />
            </div>
            <Button className="w-full" onClick={createIc} disabled={!icForm.to_company_id || !icForm.amount || !icForm.description} data-testid="ic-submit-btn">
              In beiden Firmen buchen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
